/**
 * IndexedDB cache for terrain geometry.
 * The terrain is fully deterministic so we cache the final Float32Arrays
 * (positions, colors, normals) after the first generation.
 *
 * Bump CACHE_VERSION whenever terrain generation code changes.
 */

const DB_NAME = 'terrain-cache'
const STORE_NAME = 'geometry'
const CACHE_KEY = 'planet'

// Bump this when terrainGen.ts or PlanetMesh.tsx logic changes
export const CACHE_VERSION = 1

interface CachedTerrain {
  version: number
  positions: ArrayBuffer
  colors: ArrayBuffer
  normals: ArrayBuffer
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

export async function loadCachedTerrain(): Promise<{
  positions: Float32Array
  colors: Float32Array
  normals: Float32Array
} | null> {
  try {
    const db = await openDB()
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const req = store.get(CACHE_KEY)
      req.onsuccess = () => {
        const data = req.result as CachedTerrain | undefined
        if (!data || data.version !== CACHE_VERSION) {
          resolve(null)
          return
        }
        resolve({
          positions: new Float32Array(data.positions),
          colors: new Float32Array(data.colors),
          normals: new Float32Array(data.normals),
        })
      }
      req.onerror = () => resolve(null)
    })
  } catch {
    return null
  }
}

export async function saveCachedTerrain(
  positions: Float32Array,
  colors: Float32Array,
  normals: Float32Array,
): Promise<void> {
  try {
    const db = await openDB()
    const copy = (arr: Float32Array) => {
      const buf = new ArrayBuffer(arr.byteLength)
      new Float32Array(buf).set(arr)
      return buf
    }
    const data: CachedTerrain = {
      version: CACHE_VERSION,
      positions: copy(positions),
      colors: copy(colors),
      normals: copy(normals),
    }
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const req = store.put(data, CACHE_KEY)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })
  } catch {
    // Silently fail — caching is best-effort
  }
}
